import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";

const ToastContext = createContext({ showToast: () => {} });

const ToastStack = styled.div`
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  gap: 8px;

  /* A 360px-max toast pinned 16px from the right runs off the left edge of a phone. */
  @media (max-width: 450px) {
    left: 12px;
    right: 12px;
    bottom: 12px;
  }
`;

const ToastItem = styled.div`
  background: var(--surface);
  color: var(--ink);
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: var(--shadow-md);
  padding: 10px 12px;
  min-width: 200px;
  max-width: 360px;
  font-size: 14px;

  @media (max-width: 450px) {
    min-width: 0;
    max-width: none;
  }
`;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, opts = {}) => {
    const id = Math.random().toString(36).slice(2);
    const duration = typeof opts.duration === "number" ? opts.duration : 2500;
    setToasts((prev) => [...prev, { id, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, Math.max(800, duration));
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {ReactDOM.createPortal(
        <ToastStack>
          {toasts.map((t) => (
            <ToastItem key={t.id}>
              {t.message}
            </ToastItem>
          ))}
        </ToastStack>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
